<template>
    <div>
        <h2
            class="text-lg font-medium leading-7 text-iron-700 tracking-[-0.02em]"
        >
            {{ $t("configurator.calendars") }}
        </h2>

        <p class="text-sm text-iron-500 tracking-[-0.01em]">
            {{ $t("configurator.calendars_description") }}
        </p>

        <div class="mt-6 mb-2">
            <CalendarTitleEditor
                :event-type-name="eventType.name"
                :custom-fields="eventType.customFields"
                :has-subscription="!!hasSubscription"
            />
        </div>

        <div class="mt-8 gap-1 grid">
            <div v-if="integrationCalendars.length > 0">
                <Label> {{ $t("event_types.calendars.new_bookings") }} </Label>
                <Select
                    :disabled="loading || !integrationCalendarsLoaded"
                    :model-value="selectedIntegrationCalendar"
                    @update:model-value="onSelectedIntegrationUpdate"
                >
                    <SelectTrigger class="w-full">
                        <SelectValue
                            :placeholder="
                                $t('event_types.calendars.choose_calendar')
                            "
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup
                            v-for="calendarIntegration of integrationCalendars"
                        >
                            <SelectLabel class="gap-2 pl-2 flex items-center">
                                <GoogleCalendar
                                    v-if="
                                        calendarIntegration.integration.type ===
                                        'GOOGLE'
                                    "
                                />

                                <img
                                    v-if="
                                        calendarIntegration.integration.type ===
                                        'ICLOUD_CALENDAR'
                                    "
                                    height="18"
                                    width="32"
                                    src="https://zaptime.s3.eu-central-1.amazonaws.com/application/assets/img/icloud-calendar.png"
                                />

                                <MicrosoftLogo
                                    v-if="
                                        calendarIntegration.integration.type ===
                                        'MICROSOFT'
                                    "
                                />

                                {{ calendarIntegration.integrationEmail }}
                            </SelectLabel>
                            <SelectItem
                                v-for="calendar of calendarIntegration.calendars"
                                :value="
                                    serializeSelection(
                                        calendarIntegration.integration.id,
                                        calendar.id,
                                    )
                                "
                            >
                                {{ calendar.name }}
                            </SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>
            </div>
            <div v-if="integrationCalendars.length > 0" class="mt-4">
                <Label> {{ $t("event_types.calendars.conflicts") }} </Label>

                <div
                    v-show="integrationCalendarsLoaded"
                    v-for="(integrationCalendar, i) of integrationCalendars"
                    class="border-iron-200 p-4 border-x border-t"
                    :class="[
                        i === 0 ? 'mt-2 rounded-t-lg border-t' : '',
                        integrationCalendars.length === i + 1
                            ? 'rounded-b-lg border-b'
                            : ' ',
                    ]"
                >
                    <div class="gap-3 grid grid-cols-[30px_1fr]">
                        <div>
                            <GoogleCalendar
                                v-if="
                                    integrationCalendar.integration.type ===
                                    'GOOGLE'
                                "
                            ></GoogleCalendar>
                            <img
                                v-if="
                                    integrationCalendar.integration.type ===
                                    'ICLOUD_CALENDAR'
                                "
                                height="18"
                                width="32"
                                src="https://zaptime.s3.eu-central-1.amazonaws.com/application/assets/img/icloud-calendar.png"
                            />
                            <MicrosoftLogo
                                v-if="
                                    integrationCalendar.integration.type ===
                                    'MICROSOFT'
                                "
                            />
                        </div>

                        <div class="flex flex-col">
                            <div
                                v-if="integrationCalendar.integration.name"
                                class="text-sm font-medium text-iron-700"
                            >
                                {{ integrationCalendar.integration.name }}
                            </div>
                            <div
                                class="tracking-[-1%]"
                                :class="[
                                    integrationCalendar.integration.type ===
                                        'ICLOUD_CALENDAR' ||
                                    !integrationCalendar.integration.name
                                        ? 'mb-1 mt-1 text-sm font-medium text-iron-700'
                                        : 'text-xs text-iron-500',
                                ]"
                            >
                                {{ integrationCalendar.integrationEmail }}
                            </div>

                            <div
                                class="mt-2"
                                v-for="calendar of integrationCalendar.calendars"
                                :key="calendar.id"
                            >
                                <ConflictSwitch
                                    :calendar="calendar"
                                    :integration-id="
                                        integrationCalendar.integration.id
                                    "
                                    :event-type-user="eventTypeUser"
                                    :event-type-id="eventType.id"
                                ></ConflictSwitch>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-show="!integrationCalendarsLoaded">
                <div
                    class="mt-1 rounded-md border-stone-200 p-3 shadow mx-auto w-full border"
                >
                    <div class="animate-pulse space-x-2 flex">
                        <div class="h-10 w-10 rounded-md bg-stone-200"></div>
                        <div class="space-y-6 py-1 max-w-[220px] flex-1">
                            <div class="space-y-3">
                                <div class="gap-4 grid grid-cols-3">
                                    <div
                                        class="h-3 rounded bg-stone-200 col-span-2"
                                    ></div>
                                </div>
                            </div>
                            <div class="space-y-3">
                                <div class="h-3 rounded bg-stone-200"></div>
                                <div class="h-3 rounded bg-stone-200"></div>
                                <div class="h-3 rounded bg-stone-200"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="
                    integrationCalendars.length <= 0 &&
                    integrationCalendarsLoaded
                "
            >
                <Link href="/integrations">
                    <PromoBanner :is-paid="false">
                        <template #title>{{
                            $t("event_types.calendars.banner.title")
                        }}</template>
                        <template #description>{{
                            $t("event_types.calendars.banner.description")
                        }}</template>
                        <template #icon>
                            <CalendarOff :size="20" class="text-[#E92F34]" />
                        </template>
                    </PromoBanner>
                </Link>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import axios from "axios";
import { onMounted, ref } from "vue";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import PromoBanner from "../../../Atomic/PromoBanner.vue";
import { CalendarOff } from "lucide-vue-next";
import { Link } from "@inertiajs/vue3";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { Label } from "@/components/ui/label";
import ConflictSwitch from "./components/ConflictSwitch.vue";
import CalendarTitleEditor from "./components/CalendarTitleEditor.vue";

import type {
    AssignIntegrationRequest,
    Calendar,
    IntegrationCalendar,
} from "../../types/IntegrationCalendar";
import GoogleCalendar from "../../../Integrations/components/logos/simple/GoogleCalendar.svg";
import MicrosoftLogo from "../../../Integrations/components/logos/simple/Microsoft.svg";

import type { EventType } from "../../types/EventType";
import type { EventTypeUser } from "../../types/EventTypeUser";
import { toast } from "@/components/ui/toast";
import { useHasSubscription } from "@/composables/useHasSubscription";
import {
    serializeIntegrationCalendarSelection,
    deserializeIntegrationCalendarSelection,
} from "@/utils/integrationCalendarSelection";

interface Props {
    eventType: EventType;
    eventTypeUser: EventTypeUser;
}

const props = defineProps<Props>();
const { hasSubscription } = useHasSubscription(props.eventType);

const integrationCalendars = ref<IntegrationCalendar[]>([]);
const integrationCalendarsLoaded = ref(false);
const selectedIntegrationCalendar = ref<string>();
const loading = ref(false);

function serializeSelection(
    integrationId: number,
    calendarId: string | number,
): string {
    return serializeIntegrationCalendarSelection(integrationId, calendarId);
}

function deserializeSelection(
    value: string,
): { integrationId: number; calendarId: string } | null {
    return deserializeIntegrationCalendarSelection(value);
}

onMounted(async () => {
    integrationCalendars.value = await getIntegrationCalendars();

    selectIntegrationCalendarIfExists();

    integrationCalendarsLoaded.value = true;
});

function selectIntegrationCalendarIfExists() {
    // @TODO: legacy typecheck when integration_data used to be unparsed string
    if (
        typeof props.eventTypeUser.integration_data === "string" ||
        !props.eventTypeUser.integration_data?.calendars
    ) {
        return;
    }

    console.log(props.eventTypeUser.integration_data);

    let calendarId = "";
    // check if is icloud calendar and get calendar id from caldav url
    //
    if (
        // @ts-expect-error
        props.eventTypeUser.integration_data?.calendars[0]?.calendar_url?.includes(
            "https://caldav.icloud.com",
        )
    ) {
        calendarId =
            // @ts-expect-error
            props.eventTypeUser.integration_data.calendars[0].calendar_url.replace(
                "https://caldav.icloud.com",
                "",
            );
    } else {
        calendarId = props.eventTypeUser.integration_data?.calendars[0]?.id;
    }

    const integrationId = props.eventTypeUser.integration_id;

    if (
        integrationId == null ||
        calendarId == null ||
        calendarId.toString().length === 0
    ) {
        return;
    }

    selectedIntegrationCalendar.value = serializeSelection(
        Number(integrationId),
        calendarId,
    );
}

async function getIntegrationCalendars() {
    try {
        const res = await axios.get(route("app.integrations.calendars.index"));

        console.log(res.data.data);
        return res.data.data;
    } catch (err) {
        toast({
            title: "Uh oh! Something went wrong. Please contact support.",
            description: "There was a problem with your request.",
            variant: "destructive",
        });
    }
}

function onSelectedIntegrationUpdate(value: string) {
    const parsed = deserializeSelection(value);

    if (!parsed) {
        toast({
            title: `Error when assigning calendar`,
            description: `Invalid selection payload`,
            variant: "destructive",
        });
        return;
    }

    const { integrationId, calendarId } = parsed;

    const integrationCalendar = integrationCalendars.value.find(
        (integrationCalendar) => {
            return (
                integrationCalendar.integration.id ===
                parseInt(String(integrationId))
            );
        },
    );

    if (!integrationCalendar) {
        toast({
            title: `Error when assigning calendar`,
            description: `Integration calendar not found`,
            variant: "destructive",
        });
        return;
    }

    const calendar = integrationCalendar.calendars.find(
        (calendar) => calendar.id.toString() === calendarId,
    );

    if (!calendar) {
        toast({
            title: `Error when assigning calendar`,
            description: `Calendar not found`,
            variant: "destructive",
        });
        return;
    }

    console.log(integrationId, calendar);
    assignIntegrationCalendar(parseInt(String(integrationId)), calendar);
}

function assignIntegrationCalendar(
    calendarIntegrationId: number,
    calendar: Calendar,
) {
    loading.value = true;

    const data: AssignIntegrationRequest = {
        calendars: [
            {
                integration_id: calendarIntegrationId,
                integration_calendar_id: calendar.id,
                integration_calendar_name: calendar.name,
                integration_calendar_default: calendar.default,
            },
        ],
    };

    router.post(
        route("app.event-types.calendar-integrations.assign", {
            eventType: props.eventType.id,
        }),
        data,
        {
            onFinish: () => {
                selectIntegrationCalendarIfExists();
                loading.value = false;
            },
            onError: (err) => {
                toast({
                    title: "Uh oh! Something went wrong. Please contact support.",
                    description:
                        "There was a problem with your request." +
                        JSON.stringify(err),
                    variant: "destructive",
                });
            },
        },
    );
}
</script>
